import {
  Permissions,
  TAppNameEnum,
  TFeedSpaceItem,
  TSpaceBase,
  TUserAccessMap,
  hasPermissionForApp,
} from '@onesource/schemas';

/**
 * Resolve a space's effective app. Feed/sub-spaces don't store `app` directly —
 * they inherit it from their parent container (same rule as the backend
 * `SpacesService.getApp`), so fall back to `parent.app`.
 */
export const resolveSpaceApp = (space: Pick<TSpaceBase, 'app' | 'parent'> | null | undefined): string | null =>
  space?.app ?? space?.parent?.app ?? null;

/**
 * Whether the user can manage (admin) the given space — i.e. their access entry
 * for that space's app grants `space.manage`. Mirrors the backend
 * `SpacePermissionGuard`. Use it to gate admin-only UI (admin post types,
 * pin/schedule controls) in the community hub.
 */
export const userCanManageSpace = (
  access: TUserAccessMap | undefined | null,
  space: Pick<TSpaceBase, 'app' | 'parent'> | null | undefined,
): boolean => hasPermissionForApp(access ?? undefined, resolveSpaceApp(space), Permissions.SPACE_MANAGE);

export const hasAppAccess = (access: TUserAccessMap | undefined | null, appId: TAppNameEnum): boolean => {
  const permissions = access?.[appId]?.permissions ?? [];
  return permissions.some(p => p.name === Permissions.APP_ACTIVE);
};

/**
 * Whether the user has active access (`sub.active`) for a given app key.
 * Used to gate community-hub spaces, which are keyed by their `app` string.
 */
export const hasSpaceAccess = (access: TUserAccessMap | undefined | null, app: string | null | undefined): boolean => {
  if (!app) {
    return false;
  }
  const permissions = access?.[app]?.permissions ?? [];
  return permissions.some(p => p.name === Permissions.APP_ACTIVE);
};

/**
 * Whether the user can enter the Community hub at all — i.e. they have at least
 * one community/feed space they can access. Drives both the sidebar Community
 * menu visibility and the community route guard.
 */
export const hasCommunityAccess = (feedSpaces: TFeedSpaceItem[] | undefined | null): boolean =>
  Array.isArray(feedSpaces) && feedSpaces.some(space => space.hasAccess);
